// Phase H+1 Mini-Wave 5 · V12 User Sidebar · 15+1 cluster collapsibili.
// V12 Achromatic Strict (palette ratificata · zero colori saturi).
// Wave Marketplace V10 · MP.6 · creator-conditional + badge counts.
import React, { useCallback, useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
    Activity, BarChart3, Bell, BookOpen, Bookmark, Boxes, Briefcase, Building2,
    ChevronDown, ChevronRight, ChevronsLeft, ChevronsRight,
    Coins, Compass, Database, ExternalLink, FileText, Flag, Gauge, GraduationCap, Hash,
    KeyRound, LayoutDashboard, Layers, LineChart, Lock, Mail, Palette, Plug, ScrollText,
    Search, Settings, ShieldCheck, ShoppingBag, Sparkles, Store, Tag,
    Target, Trophy, User, Users, Webhook, Workflow, Wrench, Zap,
} from "lucide-react";
import { motion } from "framer-motion";

import { V12_USER_NAV_GROUPS, V12_USER_NAV_MINIMAL, filterNavByRole } from "@/data/v12_user_nav";
import { useTranslation } from "@/i18n/I18nContext";
import { API } from "@/lib/api";

const ICON_MAP = {
    Activity, BarChart3, Bell, BookOpen, Bookmark, Boxes, Building2, Coins, Compass, Database,
    ExternalLink, FileText, Flag, Gauge, GraduationCap, Hash, KeyRound,
    LayoutDashboard, Layers, LineChart, Mail, ScrollText, Search, Settings,
    ShieldCheck, ShoppingBag, Sparkles, Store, Tag, Target, Trophy, User,
    Users, Webhook, Workflow,
};

async function _apiJsonSafe(path) {
    try {
        const r = await fetch(`${API}${path}`, { credentials: "include" });
        if (!r.ok) return null;
        return await r.json();
    } catch {
        return null;
    }
}

function _navKey(prefix, id) {
    return `${prefix}.${(id || "").replace(/-/g, "_")}`;
}

function _tOr(t, key, fallback) {
    const v = t(key);
    if (!v || v === key) return fallback;
    return v;
}

function GroupIcon({ name }) {
    const Cmp = ICON_MAP[name] || LayoutDashboard;
    return <Cmp size={16} className="stroke-zinc-400" />;
}
function ItemIcon({ name }) {
    const Cmp = ICON_MAP[name] || FileText;
    return <Cmp size={14} className="stroke-zinc-500" />;
}

function SidebarItem({ item, collapsed, badgeCount = 0 }) {
    const { pathname } = useLocation();
    const { t } = useTranslation();
    const href = item.legacyHref || item.to;
    const isLegacyExternal = !!item.legacyHref;
    const active = !isLegacyExternal && href && (pathname === href || pathname === (href || "").split("#")[0]);
    const label = _tOr(t, _navKey("user_nav", item.id), item.label);
    const common = `
        flex items-center gap-2 text-xs rounded-md transition-colors duration-200
        ${collapsed ? "justify-center px-2 py-2" : "px-3 py-1.5"}
    `.trim();

    if (!item.implemented) {
        return (
            <div
                className={`${common} text-neutral-600 cursor-not-allowed`}
                title={_tOr(t, "user_nav.coming_soon", "Prossimamente")}
                data-testid={`v12-user-sidebar-item-${item.id}`}
                data-implemented="false"
            >
                <ItemIcon name={item.icon} />
                {!collapsed && (
                    <>
                        <span className="truncate">{label}</span>
                        <Lock size={10} className="ml-auto stroke-zinc-600" />
                    </>
                )}
            </div>
        );
    }
    if (isLegacyExternal) {
        return (
            <a
                href={href}
                className={`
                    ${common}
                    text-neutral-400 v12-hover hover:text-neutral-100
                `}
                data-testid={`v12-user-sidebar-item-${item.id}`}
                data-implemented="true"
                data-legacy="true"
            >
                <ItemIcon name={item.icon} />
                {!collapsed && <span className="truncate">{label}</span>}
            </a>
        );
    }
    return (
        <Link
            to={href}
            className={`
                ${common}
                ${active
                    ? "v12-card text-neutral-100 border v12-border"
                    : "text-neutral-400 v12-hover hover:text-neutral-100"}
            `}
            data-testid={`v12-user-sidebar-item-${item.id}`}
            data-implemented="true"
        >
            <ItemIcon name={item.icon} />
            {!collapsed && <span className="truncate flex-1">{label}</span>}
            {!collapsed && badgeCount > 0 && (
                <span
                    data-testid={`v12-user-sidebar-badge-${item.id}`}
                    className="ml-1 rounded-full border v12-border bg-neutral-800 px-1.5 py-0.5 text-[9px] font-medium text-neutral-200"
                >
                    {badgeCount > 99 ? "99+" : badgeCount}
                </span>
            )}
        </Link>
    );
}

function SidebarGroupFlat({ group, collapsed, index, badges = {} }) {
    // MP.7 Hotfix R5 · Render diretto come SidebarItem top-level (no accordion).
    const only = group.items[0];
    return (
        <motion.div
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.03 * index, duration: 0.22 }}
            className="mb-3"
            data-testid={`v12-user-sidebar-group-${group.id}`}
            data-flat="true"
        >
            <SidebarItem
                item={only}
                collapsed={collapsed}
                badgeCount={only.badge ? (badges[only.badge] || 0) : 0}
            />
        </motion.div>
    );
}

function SidebarGroup({ group, collapsed, index, badges = {} }) {
    // MP.6 · Collapse state persistito in localStorage per sezione.
    // Hotfix default-collapse-all-except-first (post-login) · quando il
    // localStorage è vuoto (primo accesso) solo il PRIMO cluster (index 0)
    // appare espanso · gli altri collassati. Stato persistito dell'utente
    // (toggle manuale) ha sempre precedenza · comportamento zero-touch sul
    // salvataggio al click.
    const storageKey = `v12_nav_group_${group.id}_open`;
    const [open, setOpen] = useState(() => {
        try {
            const v = window.localStorage.getItem(storageKey);
            if (v !== null) return v === "1";          // saved state wins
            return index === 0;                        // default: only first cluster expanded
        } catch { return index === 0; }
    });
    useEffect(() => {
        try { window.localStorage.setItem(storageKey, open ? "1" : "0"); } catch {}
    }, [open, storageKey]);
    const { t } = useTranslation();
    const groupLabel = _tOr(t, _navKey("user_nav", group.id), group.label);
    return (
        <motion.div
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.03 * index, duration: 0.22 }}
            className="mb-3"
            data-testid={`v12-user-sidebar-group-${group.id}`}
        >
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                className={`
                    w-full flex items-center gap-2
                    ${collapsed ? "justify-center px-2 py-2" : "px-3 py-2"}
                    text-[11px] uppercase tracking-wider text-neutral-500
                    hover:text-neutral-200 transition-colors duration-200
                `}
                data-testid={`v12-user-sidebar-group-toggle-${group.id}`}
            >
                <GroupIcon name={group.icon} />
                {!collapsed && (
                    <>
                        <span className="truncate">{groupLabel}</span>
                        {open
                            ? <ChevronDown size={12} className="ml-auto stroke-zinc-600" />
                            : <ChevronRight size={12} className="ml-auto stroke-zinc-600" />}
                    </>
                )}
            </button>
            {open && (
                <div className="space-y-0.5">
                    {group.items.map((it) => (
                        <SidebarItem
                            key={it.id} item={it} collapsed={collapsed}
                            badgeCount={it.badge ? (badges[it.badge] || 0) : 0}
                        />
                    ))}
                </div>
            )}
        </motion.div>
    );
}

export default function V12UserSidebar({ collapsed = false, onToggle, role = "user" }) {
    const { t } = useTranslation();
    // MP.6 · fetch creator profile + badge counts (one-shot on mount + visibility)
    const [isCreator, setIsCreator] = useState(null); // null = unknown
    const [badges, setBadges] = useState({});
    const isAdminRoleLike = role === "admin" || role === "super_admin";

    const refreshContext = useCallback(async () => {
        const me = await _apiJsonSafe("/v2/marketplace/creator/me");
        const creatorNow = Boolean(me && me.creator);
        setIsCreator(creatorNow);
        const b = {};
        if (creatorNow) {
            const n = await _apiJsonSafe("/v2/marketplace/creator/notifications");
            if (n && typeof n.unread === "number") b.creator_notifications_unread = n.unread;
        }
        if (isAdminRoleLike) {
            const q = await _apiJsonSafe("/v2/marketplace/admin/moderation/queue?limit=1");
            if (q && q.counts && typeof q.counts.pending === "number") {
                b.admin_moderation_pending = q.counts.pending;
            }
        }
        setBadges(b);
    }, [isAdminRoleLike]);

    useEffect(() => { refreshContext(); }, [refreshContext]);

    const rawGroups = filterNavByRole(V12_USER_NAV_MINIMAL, role);
    // MP.6 · apply creator conditional item filter
    // Phase IX nav wire-up · apply per-item `requiredRoles` filter so
    // admin-only entries inside otherwise-shared clusters stay hidden
    // for standard users.
    const groups = rawGroups
        .map((g) => ({
            ...g,
            items: g.items.filter((it) => {
                if (it.creatorOnly && isCreator !== true) return false;
                if (it.nonCreatorOnly && isCreator === true) return false;
                if (Array.isArray(it.requiredRoles) && it.requiredRoles.length > 0) {
                    if (!it.requiredRoles.includes(role)) return false;
                }
                return true;
            }),
        }))
        .filter((g) => g.items.length > 0);
    return (
        <aside
            className={`
                sticky top-0 h-screen shrink-0
                border-r v12-border v12-sidebar-bg
                overflow-y-auto
                ${collapsed ? "w-14" : "w-60"}
                transition-[width] duration-300 ease-out
            `}
            data-testid="v12-user-sidebar"
            data-collapsed={collapsed ? "true" : "false"}
            data-role={role}
            data-is-creator={isCreator === null ? "unknown" : (isCreator ? "true" : "false")}
        >
            <div className="flex items-center justify-between px-3 py-4">
                {!collapsed && (
                    <span className="text-xs font-medium tracking-wide text-neutral-200">
                        GlobalLead · V12
                    </span>
                )}
                <button
                    type="button"
                    onClick={onToggle}
                    className="text-neutral-500 hover:text-neutral-100 p-1 transition-colors duration-200"
                    aria-label={_tOr(t, "user_nav.collapse_sidebar", "Collassa sidebar")}
                    data-testid="v12-user-sidebar-toggle"
                >
                    {collapsed
                        ? <ChevronsRight size={14} />
                        : <ChevronsLeft size={14} />}
                </button>
            </div>
            <nav className="px-2 pb-6">
                {groups.map((g, i) => {
                    // MP.7 Hotfix R5 · dispatch flat vs accordion dal parent
                    // (hooks-of-rules compliant · evita early return in SidebarGroup).
                    if (g.flat && g.items.length === 1) {
                        return (
                            <SidebarGroupFlat
                                key={g.id} group={g} collapsed={collapsed} index={i}
                                badges={badges}
                            />
                        );
                    }
                    return (
                        <SidebarGroup
                            key={g.id} group={g} collapsed={collapsed} index={i}
                            badges={badges}
                        />
                    );
                })}
            </nav>
        </aside>
    );
}
